import { 
  getElements, 
  getAllElementsNumber 
} from '../../data/FirebaseService.js';
import { ElementType } from '../../data/ElementType.js';

/**
 * Repository for managing all About Screen related data
 * Provides methods to fetch members, photos, and about information
 */
class AboutRepository {
  
  /**
   * Fetch all members from Firestore
   * @returns {Promise<Array>} Array of member objects
   */
  async getAllMembers() {
    try {
      const members = await getElements(ElementType.MemberType);
      return members || [];
    } catch (error) {
      console.error('Error fetching members:', error);
      throw new Error('Failed to fetch members');
    }
  }

  /**
   * Fetch members sorted by role (e.g., board members first, then general members)
   * Filters out Author role and sorts by ID, then moves key positions to top
   * @returns {Promise<Array>} Array of member objects sorted by role
   */
  async getMembersByRole() {
    try {
      const members = await this.getAllMembers();
      
      // Sort by ID first
      members.sort((a, b) => a.id - b.id);

      // Filter out Author role first, before reordering
      const filteredList = members.filter(member => member.role !== 'Autore');

      // Helper function to move array elements
      const arrayMove = (arr, oldIndex, newIndex) => {
        if (newIndex >= arr.length) {
          let k = newIndex - arr.length + 1;
          while (k--) {
            arr.push(undefined);
          }
        }
        arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
      };

      // Move key positions to top of the list
      let presidentIndex = filteredList.findIndex(element => element.role === 'Presidente');
      if (presidentIndex !== -1) arrayMove(filteredList, presidentIndex, 0);
      
      let vicePresidentIndex = filteredList.findIndex(element => element.role === 'Vice-Presidente');
      if (vicePresidentIndex !== -1) arrayMove(filteredList, vicePresidentIndex, 1);
      
      let secretaryIndex = filteredList.findIndex(element => element.role === 'Segretario');
      if (secretaryIndex !== -1) arrayMove(filteredList, secretaryIndex, 2);

      return filteredList;
    } catch (error) {
      console.error('Error fetching members by role:', error);
      throw new Error('Failed to fetch members by role');
    }
  }


  /**
   * Fetch all photos for gallery
   * @returns {Promise<Array>} Array of photo objects
   */
  async getAllPhotos() {
    try {
      const photos = await getAllElementsNumber(ElementType.PhotoType.elementName);
      let photosArray = [];
      for(let i = 1; i <= photos; i++) {
        photosArray.push("https://firebasestorage.googleapis.com/v0/b/consulta-giovanile-di-ortona.appspot.com/o/immagini%20sito%2Fchi%20siamo%2Fchi_siamo_" + i + ".jpg?alt=media&token=dfa9f33d-31e1-48f9-bf3d-65e2da302298");
      }
      return photosArray || [];
    } catch (error) {
      console.error('Error fetching photos:', error);
      throw new Error('Failed to fetch photos');
    }
  }

  /**
   * Fetch all data needed for About Screen in one call
   * Optimizes by making parallel requests
   * @returns {Promise<Object>} Object containing all about screen data
   */
  async getAllAboutData() {
    try {
      const [members, photos] = await Promise.all([
        this.getMembersByRole(),
        this.getAllPhotos(),
      ]);

      return {
        members,
        photos,
      };
    } catch (error) {
      console.error('Error fetching all about data:', error);
      throw new Error('Failed to fetch about screen data');
    }
  }

  /**
   * Get member statistics
   * @returns {Promise<Object>} Statistics about members
   */
  async getMemberStats() {
    try {
      const members = await this.getAllMembers();
      
      const stats = {
        total: members.filter(m => m.role !== 'Autore').length,
        boardMembers: members.filter(m => 
          m.role !== 'Autore' && ['Presidente', 'Vice-Presidente', 'Segretario', 'Tesoriere', 'Consigliere']
            .some(role => m.role?.includes(role))
        ).length,
        byRole: {}
      };

      // Count members by role (excluding Author)
      members.forEach(member => {
        if (member.role && member.role !== 'Autore') {
          stats.byRole[member.role] = (stats.byRole[member.role] || 0) + 1;
        }
      });

      return stats;
    } catch (error) {
      console.error('Error fetching member stats:', error);
      throw new Error('Failed to fetch member statistics');
    }
  }
}

// Export singleton instance
const aboutRepository = new AboutRepository();
export default aboutRepository;
